import { supabase } from '../client'
import type { CloudSubtask } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── SUBTASKS ─────────────────────────────────────────── */

export async function getCloudSubtasks(userId: string, since?: Date): Promise<CloudSubtask[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('subtasks')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('sort_order', { ascending: true })
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get subtasks error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get subtasks error', error)
    return []
  }
}

export async function getCloudSubtaskIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('subtasks')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(100000)
    if (error) {
      logSupabaseError('Get subtask IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get subtask IDs error', error)
    return []
  }
}

export async function getCloudSubtasksByIds(userId: string, ids: string[]): Promise<CloudSubtask[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('subtasks')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get subtasks by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get subtasks by IDs error', error)
    return []
  }
}

/**
 * Soft-deleted subtasks since `since`.
 *
 * `updated_at` is selected on purpose: sync-pull uses it to advance the pull
 * watermark. The older entities omit it, so their watermark never moves on a
 * deletion-only window and the next pull re-fetches it — wasteful, not lossy.
 */
export async function getDeletedCloudSubtasks(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('subtasks')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted subtasks error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted subtasks error', error)
    return []
  }
}

export async function createCloudSubtask(
  userId: string,
  subtask: Omit<CloudSubtask, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudSubtask | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('subtasks')
      .insert({ ...subtask, user_id: userId })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create subtask error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create subtask error', error)
    return null
  }
}

export async function updateCloudSubtask(
  subtaskId: string,
  updates: Partial<Omit<CloudSubtask, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('subtasks')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', subtaskId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update subtask error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update subtask error', error)
    return null
  }
}

export async function deleteCloudSubtask(subtaskId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('subtasks')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', subtaskId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete subtask error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete subtask error', error)
    return false
  }
}
